// Add / edit one SFTP destination. Password or SSH key (+ optional passphrase).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", host: "", port: 22, username: "", auth_type: "password", secret: "", passphrase: "", remote_dir: "",
  filename_pattern: "{report}-{date}-{time}.{ext}", reset_host_key: false };

function PatternField({ value, onChange }) {
  const [tokens, setTokens] = useState([]);
  const [preview, setPreview] = useState(null);
  useEffect(() => { api.get("/report-hub/sftp/filename-tokens").then((r) => setTokens(r.data.tokens || [])).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/report-hub/sftp/filename-preview", { pattern: value, report_key: "uc_cdr", name: "UC CDR weekly", ext: "csv" })
        .then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(t);
  }, [value]);
  return (
    <div className="space-y-1.5 sm:col-span-3" data-testid="sftp-pattern-field">
      <Label>File name pattern <span className="ml-1 text-[10px] font-normal text-slate-400">(sub-folders with / are created)</span></Label>
      <Input value={value} onChange={onChange} className="font-mono" placeholder="{report}-{date}-{time}.{ext}" data-testid="sftp-form-pattern" />
      <div className="flex flex-wrap gap-1" data-testid="sftp-pattern-tokens">
        {tokens.map((t) => (
          <button key={t.token} type="button" title={t.label} data-testid={`sftp-pattern-token-${t.token}`}
                  onClick={() => onChange({ target: { value: `${value}{${t.token}}` } })}
                  className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-600 hover:bg-slate-100">
            {`{${t.token}}`}
          </button>
        ))}
      </div>
      {preview && (
        <p className={`text-[11px] ${preview.ok ? "text-slate-500" : "text-rose-600"}`} data-testid="sftp-pattern-preview">
          {preview.ok ? <>e.g. <span className="font-mono text-slate-700">{preview.preview}</span> for a CSV of UC CDR</> : preview.error}
        </p>
      )}
    </div>
  );
}

export function SftpDestinationDialog({ destination, open, onClose }) {
  const editing = !!destination;
  const [form, setForm] = useState(() => (destination
    ? { ...EMPTY, ...destination, secret: "", passphrase: "" } : EMPTY));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isKey = form.auth_type === "key";

  const save = async () => {
    if (!form.name.trim() || !form.host.trim() || !form.username.trim()) { toast.error("Name, host and username are required"); return; }
    if (!editing && !form.secret.trim()) { toast.error(isKey ? "Paste the private key" : "Enter the password"); return; }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(), host: form.host.trim(), port: parseInt(form.port, 10) || 22, username: form.username.trim(),
        auth_type: form.auth_type, remote_dir: form.remote_dir.trim(), filename_pattern: (form.filename_pattern || "").trim(),
        secret: form.secret === "" ? (editing ? null : "") : form.secret,
        passphrase: isKey ? (form.passphrase === "" ? (editing ? null : "") : form.passphrase) : "",
        reset_host_key: !!form.reset_host_key,
      };
      if (editing) await api.put(`/report-hub/sftp/destinations/${destination.destination_id}`, body);
      else await api.post("/report-hub/sftp/destinations", body);
      toast.success(editing ? "Destination updated" : "Destination added — press Test to prove the connection");
      onClose(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="flex max-h-[calc(100vh-3rem)] max-w-xl flex-col gap-0 p-0" data-testid="sftp-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>{editing ? "Edit SFTP destination" : "New SFTP destination"}</DialogTitle>
          <DialogDescription>Credentials are encrypted at rest and only decrypted at upload time.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 overflow-y-auto px-6 py-4 sm:grid-cols-3">
          <div className="space-y-1.5 sm:col-span-3">
            <Label>Name</Label>
            <Input value={form.name} onChange={set("name")} placeholder="Acme finance SFTP" data-testid="sftp-form-name" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Host</Label>
            <Input value={form.host} onChange={set("host")} placeholder="sftp.acme.ca" className="font-mono" data-testid="sftp-form-host" />
          </div>
          <div className="space-y-1.5">
            <Label>Port</Label>
            <Input type="number" min={1} max={65535} value={form.port} onChange={set("port")} className="font-mono" data-testid="sftp-form-port" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Username</Label>
            <Input value={form.username} onChange={set("username")} className="font-mono" data-testid="sftp-form-username" />
          </div>
          <div className="space-y-1.5">
            <Label>Authentication</Label>
            <select value={form.auth_type} onChange={set("auth_type")} data-testid="sftp-form-auth-type"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="password">Password</option>
              <option value="key">SSH private key</option>
            </select>
          </div>
          <div className="space-y-1.5 sm:col-span-3">
            <Label>{isKey ? "Private key (PEM / OpenSSH text)" : "Password"}
              {editing && <span className="ml-1 text-[10px] text-emerald-600">(saved — leave blank to keep)</span>}</Label>
            {isKey ? (
              <Textarea rows={6} value={form.secret} onChange={set("secret")} className="font-mono text-[11px]"
                        placeholder={"-----BEGIN OPENSSH PRIVATE KEY-----\n…"} data-testid="sftp-form-secret" />
            ) : (
              <Input type="password" value={form.secret} onChange={set("secret")} data-testid="sftp-form-secret" />
            )}
          </div>
          {isKey && (
            <div className="space-y-1.5 sm:col-span-3">
              <Label>Key passphrase <span className="ml-1 text-[10px] font-normal text-slate-400">(optional)</span></Label>
              <Input type="password" value={form.passphrase} onChange={set("passphrase")} data-testid="sftp-form-passphrase" />
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-3">
            <Label>Remote folder <span className="ml-1 text-[10px] font-normal text-slate-400">(created if missing; blank = the login folder)</span></Label>
            <Input value={form.remote_dir} onChange={set("remote_dir")} placeholder="/inbound/reports" className="font-mono" data-testid="sftp-form-remote-dir" />
          </div>
          <PatternField value={form.filename_pattern || ""} onChange={set("filename_pattern")} />
          {editing && destination.host_key_fingerprint && (
            <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 sm:col-span-3" data-testid="sftp-form-reset-host-key">
              <input type="checkbox" className="mt-0.5" checked={!!form.reset_host_key}
                     onChange={(e) => setForm((f) => ({ ...f, reset_host_key: e.target.checked }))} data-testid="sftp-form-reset-host-key-input" />
              <span>
                <span className="font-semibold">Trust the new host key</span> — forget the pinned key
                <span className="font-mono"> {destination.host_key_fingerprint}</span>. Only tick this if the server was rebuilt on purpose;
                the next successful Test or upload pins whatever key it presents. Changing the host or port forgets it automatically.
              </span>
            </label>
          )}
        </div>
        <DialogFooter className="border-t border-slate-100 px-6 py-4">
          <Button variant="outline" onClick={() => onClose(false)} data-testid="sftp-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="sftp-form-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
